import i18n from "../../../i18n";
import { Trans } from "react-i18next";
import { useState, useEffect, useMemo } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ChevronRight, Search, ScrollText, Check } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { PageHeader, LoadingState, EmptyState, ui } from "../../../components/ui";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { fetchAllPolls, effectiveStatus, getCurrentUser } from "../../../services/pollServices";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { StatusPill } from "../components/StatusPill";
import { cn } from "../../../lib/utils";
import {
  ROUTES,
  POLL_STATUSES,
  RESPONSE_TYPES,
  RESPONSE_TYPE_COLOR,
  formatDate,
  describeAudience,
} from "../constants/pollOptions";

const ANY = "__any__";
const PAGE_SIZE = 10;

/** A card is a single click straight into that poll's results. */
function PollCard({ poll, onOpen }) {
  const status = effectiveStatus(poll);
  const barColor = RESPONSE_TYPE_COLOR[poll.responseType] ?? "var(--color-accent)";
  return (
    <div
      onClick={onOpen}
      className="flex items-center justify-between gap-4 px-5 py-4 bg-white rounded-xl border-l-4 shadow-[0_1px_3px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_14px_rgba(30,51,131,0.12)] hover:-translate-y-px transition cursor-pointer"
      style={{ borderLeftColor: barColor }}
    >
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-[15px] text-foreground truncate">{poll.pollTitle}</p>
        <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[13px] text-[var(--color-text-secondary)]">
          <span>{poll.createdBy?.role}</span>
          <span className="text-[var(--color-text-border)]">|</span>
          <StatusPill status={status} />
          <span className="text-[var(--color-text-border)]">|</span>
          <span>{poll.responseType}</span>
          <span className="text-[var(--color-text-border)]">|</span>
          <span>{describeAudience(poll.audience)}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 shrink-0">
        <span className="text-[13px] text-[var(--color-text-secondary)] whitespace-nowrap">
          {poll.startDateTime ? formatDate(poll.startDateTime) : "—"}
        </span>
        <ChevronRight className="h-4 w-4 text-[var(--color-accent)]" />
      </div>
    </div>
  );
}

function FilterOption({ label, active, onClick }) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-[13px] text-foreground hover:bg-[var(--color-workspace)]/60">
        <span className="truncate">{label}</span>
        {active && <Check className="h-3.5 w-3.5 shrink-0 text-[var(--color-accent)]" />}
      </button>
    </li>
  );
}

function FilterSelect({ value, onChange, placeholder, options }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-full sm:w-[160px] rounded-xl bg-white border-[var(--color-text-border)] text-[13px]">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{placeholder}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>{option}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * Creator filter with a search box and a scrollable list — the creator set can
 * be large, so a plain dropdown would be unusable. Closes on outside click.
 */
function CreatorFilter({ value, creators, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const shown = useMemo(
    () => creators.filter((c) => c.toLowerCase().includes(query.trim().toLowerCase())),
    [creators, query]
  );

  const label = value === ANY ? "All creators" : value;

  const pick = (v) => {
    onChange(v);
    setOpen(false);
    setQuery("");
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-between gap-2 h-9 w-full sm:w-[190px] px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-[13px] text-foreground">
        <span className={cn("truncate", value === ANY && "text-[var(--color-text-secondary)]")}>{label}</span>
        <ChevronRight className="h-4 w-4 rotate-90 shrink-0 text-[var(--color-text-secondary)]" />
      </button>

      {open && (
        <>
          {/* click-away layer */}
          <div className="fixed inset-0 z-[40]" onClick={() => setOpen(false)} />
          <div className="absolute z-[50] mt-1 w-[240px] max-w-[80vw] rounded-xl border border-[var(--color-text-border)] bg-white shadow-[0_16px_40px_-12px_rgba(30,51,131,0.3)]">
            <div className="p-2 border-b border-[var(--color-text-border)]/70">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--color-text-secondary)]" />
                <input
                  autoFocus
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={i18n.t("formActions.searchCreators")}
                  className="w-full h-8 pl-8 pr-2 rounded-lg border border-[var(--color-text-border)] text-[13px] outline-none focus:ring-1 focus:ring-[var(--color-accent)]" />
              </div>
            </div>
            <ul className="max-h-[240px] overflow-y-auto py-1">
              <FilterOption label="All creators" active={value === ANY} onClick={() => pick(ANY)} />
              {shown.length === 0 ? (
                <li className="px-3 py-2 text-[13px] text-[var(--color-text-secondary)] italic">
                  <Trans i18nKey="polls.noMatches" />
                </li>
              ) : (
                shown.map((c) => (
                  <FilterOption key={c} label={c} active={value === c} onClick={() => pick(c)} />
                ))
              )}
            </ul>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Screen 10 — HR Poll Log.
 * Every poll across every creator. Search by title; filter by status,
 * response type and creator (searchable). Clicking a card opens that poll's
 * results.
 */
export function HRPollLogPage() {
  const user = getCurrentUser();
  const navigate = useNavigate();

  const [polls, setPolls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(ANY);
  const [responseType, setResponseType] = useState(ANY);
  const [creator, setCreator] = useState(ANY);
  const [page, setPage] = useState(1);

  useEffect(() => {
    fetchAllPolls()
      .then(setPolls)
      .catch((err) => setError(err.message || "Failed to load the poll log."))
      .finally(() => setLoading(false));
  }, []);

  const creators = useMemo(
    () => [...new Set(polls.map((p) => p.createdBy?.role).filter(Boolean))].sort(),
    [polls]
  );

  const filtered = useMemo(
    () => {
      const statusOrder = { Active: 0, Closed: 1 };

      return polls.filter((poll) => {
        // Drafts are private to their creator — the backend already excludes
        // them from the log; this keeps them out even if that ever changes.
        if (effectiveStatus(poll) === "Draft") return false;
        if (search && !poll.pollTitle.toLowerCase().includes(search.toLowerCase())) return false;
        if (status !== ANY && effectiveStatus(poll) !== status) return false;
        if (responseType !== ANY && poll.responseType !== responseType) return false;
        if (creator !== ANY && poll.createdBy?.role !== creator) return false;
        return true;
      })
        // Preserve source order within each status, while surfacing polls
        // people can still respond to before the closed history.
        .sort((a, b) =>
          (statusOrder[effectiveStatus(a)] ?? 2) - (statusOrder[effectiveStatus(b)] ?? 2)
        );
    },
    [polls, search, status, responseType, creator]
  );

  // Narrowing the list can strand the reader on a page that no longer exists.
  useEffect(() => {
    setPage(1);
  }, [search, status, responseType, creator]);

  const totalPages = Math.max(Math.ceil(filtered.length / PAGE_SIZE), 1);
  // Clamped rather than corrected in state, so a shrinking list never renders
  // an empty page on the way to resetting itself.
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  if (!user.isHR) return <Navigate to={ROUTES.creatorList} replace />;

  const breadcrumb = [
    { label: "Home", to: "/" },
    { label: "My Polls", to: ROUTES.creatorList },
    { label: "HR Poll Log" },
  ];

  return (
    <AppLayout breadcrumb={breadcrumb}>
      <PageBackLink to={ROUTES.creatorList} label="Back to My Polls" />
      <PageHeader
        title={i18n.t("polls.hrPollLog")} />

      <div className="max-w-[1150px]">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-[320px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--color-text-secondary)]" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={i18n.t("formActions.searchByPollTitle")}
              className={cn(ui.input, "w-full pl-9 h-9")} />
          </div>
          <FilterSelect
            value={status}
            onChange={setStatus}
            placeholder={i18n.t("inventory.allStatuses")}
            options={POLL_STATUSES.filter((s) => s !== "Draft")} />
          <FilterSelect
            value={responseType}
            onChange={setResponseType}
            placeholder={i18n.t("common.allTypes")}
            options={RESPONSE_TYPES.map((t) => t.key)} />
          <CreatorFilter value={creator} creators={creators} onChange={setCreator} />
        </div>

        {loading ? (
          <div className="bg-white rounded-2xl border border-[var(--color-text-border)]/70 overflow-hidden">
            <LoadingState />
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl border border-[var(--color-text-border)]/70 overflow-hidden">
            <p className="p-6 text-sm text-[var(--color-error)]">{error}</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-white rounded-2xl border border-[var(--color-text-border)]/70 overflow-hidden">
            <EmptyState icon={ScrollText} title={i18n.t("polls.noPollsMatchTheseFilters")} />
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {visible.map((poll) => (
                <PollCard
                  key={poll.pollId}
                  poll={poll}
                  onOpen={() => navigate(`${ROUTES.creatorResults(poll.pollId)}?from=log`)} />
              ))}
            </div>
            <div className="mt-4">
              <TablePagination
                page={safePage}
                totalPages={totalPages}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPageChange={setPage} />
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
